import { useEffect, useState, useCallback } from "react";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { REVIEWS } from "../data/site";

const GLogo = () => (
    <svg viewBox="0 0 48 48" className="w-4 h-4" aria-hidden="true">
        <path
            fill="#EA4335"
            d="M24 9.5c3.5 0 6.6 1.2 9 3.2l6.7-6.7C35.6 2.4 30.1 0 24 0 14.6 0 6.5 5.4 2.6 13.3l7.8 6.1C12.3 13.9 17.7 9.5 24 9.5z"
        />
        <path
            fill="#4285F4"
            d="M46.5 24.5c0-1.5-.1-3-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.4 5.7c4.3-4 6.8-9.9 6.8-17.4z"
        />
        <path
            fill="#FBBC05"
            d="M10.4 28.6c-.6-1.7-.9-3.5-.9-5.6s.3-3.9.9-5.6l-7.8-6.1C.9 15 0 19.4 0 24s.9 9 2.6 12.7l7.8-8.1z"
        />
        <path
            fill="#34A853"
            d="M24 48c6.1 0 11.3-2 15-5.5l-7.4-5.7c-2 1.4-4.7 2.2-7.6 2.2-6.3 0-11.7-4.3-13.6-10.1l-7.8 6.1C6.5 42.6 14.6 48 24 48z"
        />
    </svg>
);

// Slider — auto-advance, prev/next, dots
export default function Reviews() {
    const [idx, setIdx] = useState(0);
    const [paused, setPaused] = useState(false);
    const total = REVIEWS.length;

    const next = useCallback(() => setIdx((i) => (i + 1) % total), [total]);
    const prev = useCallback(
        () => setIdx((i) => (i - 1 + total) % total),
        [total]
    );

    useEffect(() => {
        if (paused) return;
        const t = setInterval(next, 5500);
        return () => clearInterval(t);
    }, [next, paused]);

    // 3-card window: show idx, idx+1, idx+2 on desktop; only idx on mobile
    const cardAt = (offset) => REVIEWS[(idx + offset) % total];

    return (
        <section
            id="yorumlar"
            data-testid="reviews-section"
            className="section bg-[#0a0a0c] gold-glow relative"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
        >
            <div className="container-x relative">
                <div className="text-center max-w-2xl mx-auto mb-12 md:mb-16">
                    <div className="eyebrow mb-4">Google Yorumları</div>
                    <h2 className="h2 text-[#f4efe4]">
                        Misafirlerimizin{" "}
                        <span className="gold-text">Deneyimleri</span>
                    </h2>
                    <div className="divider-gold mt-6 mx-auto" />
                    <div className="mt-8 flex flex-wrap gap-3 items-center justify-center">
                        <div className="inline-flex items-center gap-3 card-flat rounded-full px-5 py-2.5">
                            <GLogo />
                            <span className="text-[14px] text-[#f4efe4] font-semibold">
                                4.9
                            </span>
                            <span className="inline-flex items-center gap-0.5">
                                {[...Array(5)].map((_, i) => (
                                    <Star
                                        key={i}
                                        className="w-3.5 h-3.5 star"
                                    />
                                ))}
                            </span>
                            <span className="text-[12px] text-[#7d766a]">
                                · 500+ yorum
                            </span>
                        </div>
                        <a
                            href="/yorum-birak"
                            data-testid="leave-review-link"
                            className="inline-flex items-center gap-2 text-[12.5px] text-[#c9a24b] hover:text-[#f5d685] transition font-medium underline underline-offset-4 decoration-[#c9a24b]/40 hover:decoration-[#f5d685]"
                        >
                            Yorum bırakmak ister misiniz?
                        </a>
                    </div>
                </div>

                {/* Slider */}
                <div className="relative" data-testid="reviews-slider">
                    {/* Cards row */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                        {[0, 1, 2].map((offset) => {
                            const r = cardAt(offset);
                            const active = offset === 0;
                            return (
                                <article
                                    key={`${idx}-${offset}`}
                                    data-testid={`review-slide-${offset}`}
                                    className={`review-card bg-[#17171b] border rounded-2xl p-6 md:p-7 flex flex-col relative overflow-hidden transition-all duration-500 ${
                                        active
                                            ? "border-[#c9a24b]/45 shadow-[0_20px_50px_-20px_rgba(201,162,75,0.35)]"
                                            : "border-[#26262c] opacity-90"
                                    } ${offset > 0 ? "hidden md:flex" : "flex"} ${
                                        offset === 2 ? "lg:flex hidden md:hidden" : ""
                                    }`}
                                >
                                    <Quote className="absolute top-4 right-4 w-16 h-16 text-[#c9a24b]/8" />

                                    <div className="flex items-start justify-between mb-4 relative">
                                        <div className="inline-flex items-center gap-0.5">
                                            {[...Array(r.rating)].map((_, j) => (
                                                <Star
                                                    key={j}
                                                    className="w-4 h-4 star"
                                                />
                                            ))}
                                        </div>
                                        <GLogo />
                                    </div>

                                    <p className="text-[14px] leading-relaxed text-[#c9c2b3] flex-1 relative">
                                        "{r.text}"
                                    </p>

                                    <div className="mt-5 pt-4 border-t border-[#23232b] flex items-center justify-between">
                                        <div>
                                            <div className="text-[14px] font-medium text-[#f4efe4]">
                                                {r.name}
                                            </div>
                                            <div className="text-[11.5px] text-[#7d766a] mt-0.5 uppercase tracking-wider">
                                                Google · {r.date}
                                            </div>
                                        </div>
                                        <div className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full border border-[#c9a24b]/25 text-[#c9a24b]">
                                            Doğrulandı
                                        </div>
                                    </div>
                                </article>
                            );
                        })}
                    </div>

                    {/* Controls */}
                    <div className="mt-8 flex items-center justify-center gap-4">
                        <button
                            onClick={prev}
                            aria-label="Önceki yorum"
                            data-testid="reviews-prev"
                            className="w-11 h-11 rounded-full border border-[#26262c] hover:border-[#c9a24b] hover:bg-[#c9a24b]/10 text-[#c9c2b3] hover:text-[#f5d685] flex items-center justify-center transition"
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </button>

                        {/* Dots */}
                        <div
                            className="flex items-center gap-2"
                            data-testid="reviews-dots"
                        >
                            {REVIEWS.map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => setIdx(i)}
                                    aria-label={`Yorum ${i + 1}`}
                                    data-testid={`reviews-dot-${i}`}
                                    className={`transition-all rounded-full ${
                                        i === idx
                                            ? "w-8 h-1.5 bg-gradient-to-r from-[#f5d685] to-[#c9a24b]"
                                            : "w-1.5 h-1.5 bg-[#26262c] hover:bg-[#7d766a]"
                                    }`}
                                />
                            ))}
                        </div>

                        <button
                            onClick={next}
                            aria-label="Sonraki yorum"
                            data-testid="reviews-next"
                            className="w-11 h-11 rounded-full border border-[#26262c] hover:border-[#c9a24b] hover:bg-[#c9a24b]/10 text-[#c9c2b3] hover:text-[#f5d685] flex items-center justify-center transition"
                        >
                            <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
}
